import { toast } from "react-toastify";
import Button from "../../components/Button";
import { withTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux/es/exports";
import { MdOutlineDataSaverOff } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import ToggleWithLabel from "../../components/ToggleWithLabel";
import {
  getAppVersions,
  getPlatforms,
  saveAppVersion,
} from "../../services/appVersionService";

const AddAndEditAppVersion = ({ t }) => {
  const { token } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const { platform } = useParams();

  const [form, setForm] = useState({
    platform: "",
    latestVersion: "",
    minVersion: "",
    storeUrl: "",
    releaseNotes: "",
  });

  const [forceUpdate, setForceUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [platforms, setPlatforms] = useState([]);

  // Platform options for the "add" dropdown.
  useEffect(() => {
    (async () => {
      const response = await getPlatforms(token);
      if (response?.success) {
        setPlatforms(response.data);
      }
    })();
  }, [token]);

  // Edit mode: there is no getById endpoint, so load the list and pick this platform.
  useEffect(() => {
    if (platform) {
      (async () => {
        const response = await getAppVersions(token);
        if (response?.success) {
          const current = response.data.find(
            (item) => item.platform === platform
          );
          if (current) {
            setForm({
              platform: current.platform || "",
              latestVersion: current.latestVersion || "",
              minVersion: current.minVersion || "",
              storeUrl: current.storeUrl || "",
              releaseNotes: current.releaseNotes || "",
            });
            setForceUpdate(!!current.forceUpdate);
          }
        }
      })();
    }
  }, [platform, token]);

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setIsLoading(true);

    const params = {
      platform: platform || form.platform,
      latestVersion: form.latestVersion,
      minVersion: form.minVersion || null,
      forceUpdate: forceUpdate,
      storeUrl: form.storeUrl || null,
      releaseNotes: form.releaseNotes || null,
    };

    try {
      const response = await saveAppVersion(token, params);

      if (response?.success) {
        navigate("/app-versions");
        toast.success(
          platform
            ? t("appVersions.action.edit_success")
            : t("appVersions.action.add_success"),
          {
            position: "bottom-right",
            autoClose: 3000,
            closeOnClick: true,
            className: "add-success",
          }
        );
      } else {
        toast.error(response?.message || t("appVersions.action.error"), {
          position: "bottom-right",
          autoClose: 3000,
          closeOnClick: true,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-4 py-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-semibold text-gray-900">
          {platform ? t("appVersions.update") : t("appVersions.add")}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="overflow-hidden shadow sm:rounded-md">
          <div className="px-4 py-5 bg-white sm:p-6">
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="platform"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("appVersions.platform")}
                </label>
                <select
                  id="platform"
                  name="platform"
                  required
                  disabled={!!platform}
                  value={platform || form.platform}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, platform: e.target.value }))
                  }
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100"
                >
                  <option value="">{t("appVersions.select_platform")}</option>
                  {platforms.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="latestVersion"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("appVersions.latest_version")}
                </label>
                <input
                  type="text"
                  name="latestVersion"
                  id="latestVersion"
                  required
                  placeholder="0.0.4"
                  value={form.latestVersion}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      latestVersion: e.target.value,
                    }))
                  }
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="minVersion"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("appVersions.min_version")}
                </label>
                <input
                  type="text"
                  name="minVersion"
                  id="minVersion"
                  placeholder="0.0.1"
                  value={form.minVersion}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      minVersion: e.target.value,
                    }))
                  }
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="storeUrl"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("appVersions.store_url")}
                </label>
                <input
                  type="text"
                  name="storeUrl"
                  id="storeUrl"
                  value={form.storeUrl}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, storeUrl: e.target.value }))
                  }
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6">
                <label
                  htmlFor="releaseNotes"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("appVersions.release_notes")}
                </label>
                <textarea
                  name="releaseNotes"
                  id="releaseNotes"
                  rows={4}
                  value={form.releaseNotes}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      releaseNotes: e.target.value,
                    }))
                  }
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>

              <div className="col-span-6 grid gap-y-4">
                <ToggleWithLabel
                  label={t("appVersions.force_update")}
                  enabled={forceUpdate}
                  setEnabled={setForceUpdate}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end px-4 py-3 text-right bg-gray-50 sm:px-6">
            <Button type="button" to="/app-versions" btnName={t("backward")} />
            <button
              type="submit"
              className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-1.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 inline-flex items-center ml-3"
            >
              {t("save")}
              {isLoading ? (
                <svg
                  role="status"
                  className="inline w-4 h-4 ml-1 text-white animate-spin"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="#E5E7EB"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <MdOutlineDataSaverOff className="w-4 h-4 ml-1" />
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default withTranslation()(AddAndEditAppVersion);
